import { apiBase, fetchWithAuth } from "@/api/rest/core/apiClient";
import { readTextStream } from "@/api/rest/core/readTextStream";

export type AnalyticsScopeType = "partner_code" | "all";

export interface AnalyticsScopeOption {
  type: "partner_code";
  value: string;
  label: string;
}

export interface AnalyticsScopesResponse {
  canAccess: boolean;
  accessError?: string | null;
  scopes: AnalyticsScopeOption[];
  canAccessAll: boolean;
}

export interface AnalyticsChatRequest {
  conversationId: string;
  scopeType: AnalyticsScopeType;
  scopeValue?: string;
  message: string;
}

export interface AnalyticsConversationMessage {
  role: "user" | "assistant";
  content: string;
}

export interface AnalyticsConversationResponse {
  conversationId?: string | null;
  messages: AnalyticsConversationMessage[];
  activeTurn?: AnalyticsTurnData | null;
}

export interface AnalyticsSseEvent {
  event: "snapshot" | "activity" | "message" | "error" | "done";
  data: string;
}

export type AnalyticsActivityKind = "commentary" | "command" | "output";

export interface AnalyticsActivityData {
  id: string;
  kind: AnalyticsActivityKind;
  text: string;
  status?: string | null;
}

export interface AnalyticsMessageData {
  content: string;
}

export interface AnalyticsErrorData {
  message: string;
}

export type AnalyticsTurnStatus = "queued" | "running" | "completed" | "failed" | "cancelled";

export interface AnalyticsTurnData {
  turnId: string;
  userMessage: string;
  status: AnalyticsTurnStatus;
  activities: AnalyticsActivityData[];
  finalAnswer?: string | null;
  error?: string | null;
  startedAt: string;
  completedAt?: string | null;
}

/**
 * Send a question to the analytics service and stream SSE response.
 */
export const getAnalyticsScopes = async (): Promise<AnalyticsScopesResponse> => {
  const response = await fetchWithAuth(`${apiBase}/api/analytics/scopes`);

  if (!response.ok) {
    throw new Error(`Analytics scopes request failed: ${response.status}`);
  }

  return (await response.json()) as AnalyticsScopesResponse;
};

export const getAnalyticsConversation = async (
  scopeType: AnalyticsScopeType,
  scopeValue?: string,
): Promise<AnalyticsConversationResponse> => {
  const params = new URLSearchParams({ scopeType });
  if (scopeValue) {
    params.set("scopeValue", scopeValue);
  }

  const response = await fetchWithAuth(`${apiBase}/api/analytics/conversation?${params.toString()}`);

  if (!response.ok) {
    throw new Error(`Analytics conversation request failed: ${response.status}`);
  }

  return (await response.json()) as AnalyticsConversationResponse;
};

export const clearAnalyticsConversation = async (scopeType: AnalyticsScopeType, scopeValue?: string): Promise<void> => {
  const params = new URLSearchParams({ scopeType });
  if (scopeValue) {
    params.set("scopeValue", scopeValue);
  }

  const response = await fetchWithAuth(`${apiBase}/api/analytics/conversation?${params.toString()}`, {
    method: "DELETE",
  });

  if (!response.ok) {
    throw new Error(`Clear analytics conversation failed: ${response.status}`);
  }
};

export const askAnalyticsQuestion = async (
  request: AnalyticsChatRequest,
  onEvent: (event: AnalyticsSseEvent) => void,
  signal?: AbortSignal,
): Promise<void> => {
  const response = await fetchWithAuth(`${apiBase}/api/analytics/chat`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
    },
    body: JSON.stringify(request),
    signal,
  });

  if (!response.ok) {
    throw new Error(`Analytics request failed: ${response.status}`);
  }

  await readAnalyticsEventStream(response, onEvent);
};

export const stopAnalyticsTurn = async (turnId: string): Promise<AnalyticsTurnData> => {
  const response = await fetchWithAuth(`${apiBase}/api/analytics/turns/${turnId}/stop`, {
    method: "POST",
  });

  if (!response.ok) {
    throw new Error(`Stop analytics turn failed: ${response.status}`);
  }

  return (await response.json()) as AnalyticsTurnData;
};

export const streamAnalyticsTurn = async (
  turnId: string,
  onEvent: (event: AnalyticsSseEvent) => void,
  signal?: AbortSignal,
): Promise<void> => {
  const response = await fetchWithAuth(`${apiBase}/api/analytics/turns/${turnId}/stream`, {
    headers: {
      Accept: "text/event-stream",
    },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Analytics turn stream failed: ${response.status}`);
  }

  await readAnalyticsEventStream(response, onEvent);
};

// SSE: events are separated by a blank line. The last event may arrive without one when the stream closes.
export const readAnalyticsEventStream = async (
  response: Response,
  onEvent: (event: AnalyticsSseEvent) => void,
): Promise<void> => {
  if (!response.body) {
    throw new Error("No response body");
  }

  let buffer = "";
  await readTextStream(response.body, (text) => {
    buffer += text;
    const blocks = buffer.split("\n\n");
    buffer = blocks.pop() ?? ""; // Keep the incomplete event in the buffer
    blocks.forEach((block) => emitSseEvent(block, onEvent));
  });
  emitSseEvent(buffer, onEvent);
};

const emitSseEvent = (eventBlock: string, onEvent: (event: AnalyticsSseEvent) => void): void => {
  if (!eventBlock.trim()) {
    return;
  }

  let eventName = "";
  const eventDataParts: string[] = [];

  for (const line of eventBlock.split("\n")) {
    if (line.startsWith("event:")) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      eventDataParts.push(line.slice(5).trim());
    }
  }

  if (eventName) {
    onEvent({
      event: eventName as AnalyticsSseEvent["event"],
      data: eventDataParts.join("\n"),
    });
  }
};

/**
 * Parse an SSE data payload as JSON.
 */
export const parseAnalyticsEventData = <T>(data: string): T | null => {
  if (!data || data === "") {
    return null;
  }
  try {
    return JSON.parse(data) as T;
  } catch {
    return null;
  }
};
